import { useRef, useState } from 'react';
import { CheckCircle2, PenLine, Upload } from 'lucide-react';
import {
  Alert, Button, Card, CardBody, CardHeader, EmptyState, ErrorState, StatusBadge, TextField, TextSkeleton, cn, useIdempotencyKey, useToast,
} from '@dru/ui';
import { api } from '../lib/api';
import { useQuery } from '../lib/useQuery';
import { errorMessage, useAuth } from '../lib/auth';
import { formatDateTime } from '../lib/format';
import { PageHeader } from '../layouts/ConsoleShell';

interface MySignatureView {
  fullName: string | null;
  current: { id: string; version: number; width: number; height: number; createdAt: string; preview: string } | null;
  history: Array<{ id: string; version: number; createdAt: string; retiredAt: string | null; correctionReason: string | null }>;
  professional: { designation: string | null; qualification: string | null; registrationNo: string | null; registrationCouncil: string | null };
  availableForSigning: boolean;
  signingActs: string[];
}

/**
 * Crops the empty margin around the ink and returns a PNG — whatever came in
 * (PNG or JPG). Runs in the browser so the person sees exactly what will be
 * stored before it is sent; the server checks and trims again, and re-encodes
 * the file itself, so nothing but pixels is ever kept. Never scales.
 */
async function preparePng(file: File): Promise<{ dataUrl: string; width: number; height: number }> {
  if (!/^image\/(png|jpeg)$/.test(file.type)) throw new Error('Choose a PNG or JPG image of your signature.');
  if (file.size > 4 * 1024 * 1024) throw new Error('The file is larger than 4 MB. Use a smaller photo or scan.');
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image(); i.onload = () => resolve(i); i.onerror = () => reject(new Error('That file could not be read as an image.')); i.src = url;
    });
    // A phone photo can be enormous: bring it to a working size first, keeping its shape.
    const scale = Math.min(1, 1600 / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.round(img.naturalWidth * scale); const h = Math.round(img.naturalHeight * scale);
    const canvas = document.createElement('canvas'); canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(img, 0, 0, w, h);
    const px = ctx.getImageData(0, 0, w, h).data;
    let minX = w; let minY = h; let maxX = -1; let maxY = -1;
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      const o = (y * w + x) * 4;
      if (px[o + 3]! > 24 && (px[o]! + px[o + 1]! + px[o + 2]!) / 3 < 200) {
        if (x < minX) minX = x; if (x > maxX) maxX = x; if (y < minY) minY = y; if (y > maxY) maxY = y;
      }
    }
    if (maxX < 0) throw new Error('No signature was found in the image — it looks blank.');
    const pad = 8;
    minX = Math.max(0, minX - pad); minY = Math.max(0, minY - pad); maxX = Math.min(w - 1, maxX + pad); maxY = Math.min(h - 1, maxY + pad);
    const out = document.createElement('canvas'); out.width = maxX - minX + 1; out.height = maxY - minY + 1;
    const octx = out.getContext('2d')!;
    octx.drawImage(canvas, minX, minY, out.width, out.height, 0, 0, out.width, out.height);
    // Paper becomes transparent so the signature sits on the report like ink.
    const data = octx.getImageData(0, 0, out.width, out.height);
    for (let i = 0; i < data.data.length; i += 4) {
      if ((data.data[i]! + data.data[i + 1]! + data.data[i + 2]!) / 3 >= 225) data.data[i + 3] = 0;
    }
    octx.putImageData(data, 0, 0);
    return { dataUrl: out.toDataURL('image/png'), width: out.width, height: out.height };
  } finally { URL.revokeObjectURL(url); }
}

/**
 * Your own signature, for the reports you perform, verify or review.
 *
 * Only you upload it, and it is only ever placed on a report by an act you
 * perform yourself — entering results, verifying, reviewing. Having one on
 * file signs nothing.
 */
/**
 * Acts that put this person's name on a report as the one who verified or
 * signed it — where a registration number is printed under the signature.
 * Entering a result alone ("performed by") does not need one.
 */
const SIGNING_ACTS = (acts: string[]) => acts.some((a) => a !== 'lab.result-enter' && a !== 'encounter.station-enter');

export function MySignature() {
  const { permissions } = useAuth();
  // A login whose work signs nothing has no profile to set up. It is told so
  // plainly instead of being shown the API's "missing permission" error.
  if (!permissions.has('signature.manage-own')) {
    return (
      <div className="max-w-2xl space-y-5">
        <PageHeader title="My signature" />
        <EmptyState
          icon={<PenLine className="h-6 w-6" aria-hidden />}
          title="Signatures are for staff who sign reports"
          hint="Laboratory technicians, pathologists and physicians keep a signature here for the reports they enter, verify or review. Your role does not sign reports, so there is nothing to set up."
        />
      </div>
    );
  }
  return <SigningProfile />;
}

function SigningProfile() {
  const view = useQuery<MySignatureView>(() => api.get('/signatures/mine'), []);
  const [draft, setDraft] = useState<{ dataUrl: string; width: number; height: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [details, setDetails] = useState<MySignatureView['professional'] | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const idem = useIdempotencyKey();
  const toast = useToast();

  if (view.error) return <ErrorState detail={view.error} onRetry={view.reload} />;
  const v = view.data;
  const prof = details ?? v?.professional ?? { designation: null, qualification: null, registrationNo: null, registrationCouncil: null };

  async function pick(file: File | undefined) {
    if (!file) return;
    setError(null);
    try { setDraft(await preparePng(file)); } catch (e) { setError(errorMessage(e)); }
    if (fileRef.current) fileRef.current.value = '';
  }

  async function save() {
    if (!draft) return;
    setBusy(true); setError(null);
    try {
      await api.post('/signatures/mine', { base64: draft.dataUrl.split(',')[1], mimeType: 'image/png' }, idem.key());
      setDraft(null); idem.rotate(); view.reload();
      toast.toast('success', 'Signature saved', 'Reports already issued keep the signature they were issued with.');
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  async function saveDetails() {
    setBusy(true); setError(null);
    try { await api.put('/signatures/mine/professional', prof); setDetails(null); view.reload(); toast.toast('success', 'Details saved'); }
    catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  return (
    <div className="max-w-2xl space-y-5">
      <PageHeader
        title="My signature"
        hint="Printed on a report only beside an act you perform yourself — entering results, verifying a report, or reviewing it as the physician. Nobody else can place it."
      />
      {error && <Alert tone="danger">{error}</Alert>}

      {v && <ProfileSummary v={v} />}

      <Card>
        <CardHeader
          title="Signature image"
          hint="A PNG with a transparent background prints best. A photo or scan (JPG or PNG) is cropped and converted for you."
          actions={view.loading ? null : (
            <StatusBadge tone={v?.availableForSigning ? 'success' : 'warning'} dot>
              {v?.availableForSigning ? 'Available for signing' : v?.current ? 'On file — no signing role' : 'Not uploaded'}
            </StatusBadge>
          )}
        />
        <CardBody className="space-y-4">
          {view.loading && <TextSkeleton lines={3} />}
          {v?.current && !draft && (
            <div>
              <div className="flex min-h-[96px] items-center justify-center rounded-input border border-neutral-200 bg-[repeating-conic-gradient(#f4f5f7_0_25%,#fff_0_50%)] bg-[length:16px_16px] p-4">
                <img src={v.current.preview} alt="Your current signature" className="max-h-24 max-w-full" />
              </div>
              <p className="mt-1.5 text-caption text-neutral-500">
                Version {v.current.version} · {v.current.width}×{v.current.height} px · uploaded {formatDateTime(v.current.createdAt)}
              </p>
            </div>
          )}
          {draft && (
            <div>
              <p className="mb-1.5 text-small font-semibold text-neutral-800">Preview — cropped, not resized</p>
              <div className="flex min-h-[96px] items-center justify-center rounded-input border-2 border-dashed border-primary-300 bg-[repeating-conic-gradient(#f4f5f7_0_25%,#fff_0_50%)] bg-[length:16px_16px] p-4">
                <img src={draft.dataUrl} alt="New signature preview" className="max-h-24 max-w-full" />
              </div>
              <p className="mt-1.5 text-caption text-neutral-500">{draft.width}×{draft.height} px. Check that nothing is cut off before saving.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button onClick={save} loading={busy} icon={<CheckCircle2 className="h-4 w-4" aria-hidden />}>
                  {v?.current ? 'Replace signature' : 'Save signature'}
                </Button>
                <Button variant="secondary" onClick={() => setDraft(null)} disabled={busy}>Cancel</Button>
              </div>
            </div>
          )}
          {!draft && !view.loading && (
            <div>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg" className="sr-only" id="sig-file"
                onChange={(e) => void pick(e.target.files?.[0])} />
              <Button variant="secondary" onClick={() => fileRef.current?.click()} icon={<Upload className="h-4 w-4" aria-hidden />}>
                {v?.current ? 'Upload a replacement' : 'Upload your signature'}
              </Button>
              <p className="mt-2 text-caption text-neutral-500">
                Sign on plain white paper with a dark pen, then photograph or scan it. A replacement is a new version:
                reports already issued keep the exact signature they were issued with.
              </p>
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="How you are named on reports" hint="Printed under your signature. Enter only what is true — your role on a report comes from what you did, not from this text." />
        <CardBody className="grid gap-3 sm:grid-cols-2">
          <TextField label="Designation" hint="e.g. Consultant Pathologist, Laboratory Technician" value={prof.designation ?? ''} onChange={(v) => setDetails({ ...prof, designation: v })} />
          <TextField label="Qualification" hint="e.g. MD Pathology, DMLT" value={prof.qualification ?? ''} onChange={(v) => setDetails({ ...prof, qualification: v })} />
          <TextField label="Registration number" hint="Where your profession requires one" value={prof.registrationNo ?? ''} onChange={(v) => setDetails({ ...prof, registrationNo: v })} />
          <TextField label="Registering council" value={prof.registrationCouncil ?? ''} onChange={(v) => setDetails({ ...prof, registrationCouncil: v })} />
          <div className="sm:col-span-2">
            <Button onClick={saveDetails} loading={busy} disabled={!details} icon={<PenLine className="h-4 w-4" aria-hidden />}>Save details</Button>
          </div>
        </CardBody>
      </Card>

      {!!v?.history.length && v.history.length > 1 && (
        <Card>
          <CardHeader title="Earlier versions" hint="Kept, because reports issued with them still point at them." />
          <CardBody>
            <ul className="divide-y divide-neutral-200 text-small">
              {v.history.map((h) => (
                <li key={h.id} className="flex justify-between gap-3 py-2">
                  <span>Version {h.version}{h.correctionReason ? ' · administrative correction' : ''}</span>
                  <span className="text-neutral-500">{formatDateTime(h.createdAt)}{h.retiredAt ? ` – replaced ${formatDateTime(h.retiredAt)}` : ' · current'}</span>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}
    </div>
  );
}

/**
 * The profile at a glance: what is on file, what is missing, and the one
 * thing people get wrong — a signature on file is not permission to sign.
 */
function ProfileSummary({ v }: { v: MySignatureView }) {
  const needsRegistration = SIGNING_ACTS(v.signingActs);
  const items: Array<{ label: string; value: string | null; required: boolean }> = [
    { label: 'Full name', value: v.fullName, required: true },
    { label: 'Professional role', value: v.professional.designation, required: true },
    { label: 'Qualification', value: v.professional.qualification, required: true },
    {
      label: 'Registration',
      value: v.professional.registrationNo
        ? [v.professional.registrationNo, v.professional.registrationCouncil].filter(Boolean).join(' · ') : null,
      required: needsRegistration,
    },
    { label: 'Signature image', value: v.current ? `Version ${v.current.version}` : null, required: true },
  ];
  // Policy (lab-report readiness, SIGNATURE_IMAGE_MISSING / REGISTRATION_MISSING):
  // advice, never a block. So these are "recommended", not "required".
  const missing = items.filter((i) => i.required && !i.value);
  return (
    <Card>
      <CardHeader
        title="Your signing profile"
        actions={(
          <StatusBadge tone={missing.length ? 'warning' : 'success'} dot>
            {missing.length ? `${missing.length} recommended item${missing.length === 1 ? '' : 's'} missing` : 'Profile complete'}
          </StatusBadge>
        )}
      />
      <CardBody className="space-y-3">
        <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
          {items.map((i) => (
            <div key={i.label} className="min-w-0">
              <dt className="text-caption text-neutral-500">{i.label}{!i.required && ' (not needed for your role)'}</dt>
              <dd className={cn('text-small', i.value ? 'text-neutral-900' : i.required ? 'font-medium text-warning-600' : 'text-neutral-500')}>
                {i.value ?? (i.required ? 'Not on file — recommended' : 'Not recorded')}
              </dd>
            </div>
          ))}
        </dl>
        {v.current && (
          <div className="flex items-center gap-3 rounded-input border border-neutral-200 px-3 py-2">
            <img src={v.current.preview} alt="Your signature on file" className="max-h-10 max-w-[10rem]" />
            <span className="text-caption text-neutral-500">Printed beside acts you perform yourself.</span>
          </div>
        )}
        {missing.length > 0 && (
          <p className="text-caption text-neutral-600">
            You can still sign without these: with no image your box prints “Signed electronically · no signature image on file”,
            and with no registration number none is printed under your name.
          </p>
        )}
        <p className="text-caption text-neutral-600">
          Uploading a signature is not permission to sign. What you may enter, verify or sign comes from your role;
          your signature is placed on a report only when you perform that act yourself, and a report already issued keeps the
          signature and details it was issued with.
        </p>
      </CardBody>
    </Card>
  );
}
